import { Autocomplete, Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Tooltip, Typography } from "@mui/material";
import React, { useState, useEffect } from 'react';
import TooltipNumber from "./basicData/TooltipNumber";
import TooltipInvoiceType from "./basicData/TooltipInvoiceType";
import BasicDataMessageError from "./basicData/BasicDataMessageError";
import { getResourcePlacesSearch } from "../../api/resourcePlaces";
import { getWorksSearch } from "../../api/works";
import ErrorBox from "../../../components/shared/boxes/ErrorBox";

const InvoiceBasicData = ({ data, setData, onValidityChange, hasWorksSelected, accountId }) => {
    const [basicDataError, setBasicDataError] = useState(false);
    const [clientOptions, setClientOptions] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [loadingClients, setLoadingClients] = useState(false);
    const [configClientsApiError, setConfigClientsApiError] = useState(null);

    useEffect(() => {
        if (!accountId) {
            setClientOptions([]);
            return;
        }

        const fetchClients = async () => {
            setLoadingClients(true);
            try {
                const params = { viewMode: ['Customer'], accountId };
                if (searchText && searchText.trim() !== '') {
                    params.q = searchText.trim();
                }

                const result = await getResourcePlacesSearch(params);

                const options = result.results
                    .map(item => ({ ...item, label: item.name }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                setClientOptions(options);
            } catch (error) {
                setConfigClientsApiError(error.message);
            } finally {
                setLoadingClients(false);
            }
        };

        if (searchText === '') {
            fetchClients();
        } else {
            const timeoutId = setTimeout(fetchClients, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [searchText, accountId]);


    useEffect(() => {
        if (!data.client?.id) {
            setBasicDataError(false);
            return;
        }

        const fetchWorks = async () => {
            try {
                const result = await getWorksSearch({ rootIds: [data.client.id] });
                const worksData = Array.isArray(result?.results) ? result.results : [];

                setBasicDataError(worksData.length === 0);
            } catch (err) {
                setBasicDataError(true);
            }
        };

        fetchWorks();
    }, [data.client]);

    useEffect(() => {
        const isValid = !!data?.client
            && !!data?.invoiceType
            && (data.invoiceType === "without_work" || !basicDataError)
            && !configClientsApiError;

        onValidityChange?.(isValid);
    }, [data.client, data.invoiceType, basicDataError, onValidityChange, configClientsApiError]);

    return (
        <Box sx={{ p: '3rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            {basicDataError && data.invoiceType === 'has_work' && (
                <BasicDataMessageError />
            )}
            {configClientsApiError && (
                <ErrorBox
                    errorMessage={configClientsApiError}
                    onClose={() => setConfigClientsApiError(null)}
                />
            )}
            <Box>
                <TooltipNumber />
                <Tooltip title="Não é possível alterar o número da fatura" placement='right' arrow>
                    <TextField disabled placeholder="-" variant="outlined" />
                </Tooltip>
            </Box>
            <Box>
                <FormLabel sx={{ color: 'text.primary' }}><b> Cliente </b><FormLabel sx={{color:'error.main'}} ><b>*</b></FormLabel></FormLabel>
                <Tooltip
                    title={hasWorksSelected ? "Não é possível alterar essa informação porque já existem itens vinculados à fatura." : null}
                    placement="right"
                    arrow
                    disableHoverListener={!hasWorksSelected}
                >
                    <Autocomplete
                        onInputChange={(e, newInputValue) => setSearchText(newInputValue)}
                        disablePortal
                        disabled={hasWorksSelected}
                        options={clientOptions}
                        getOptionLabel={(option) => option?.label || ""}
                        value={data.client || null}
                        onChange={(e, newValue) =>
                            setData((prevState) => ({
                                ...prevState,
                                client: newValue,
                            }))
                        }
                        sx={{ width: 300 }}
                        slotProps={{
                            listbox: {
                                sx: {
                                    maxHeight: '12rem',
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Pesquisar"
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props}>{option.label}</li>
                        )}
                        noOptionsText={!loadingClients ? "Nenhum resultado encontrado" : "Carregando..."}
                    />
                </Tooltip>
            </Box>
            <Box>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <FormLabel sx={{ color: 'text.primary' }}><b> Tipo de Fatura </b><FormLabel sx={{color:'error.main'}}><b>*</b></FormLabel></FormLabel>
                    <TooltipInvoiceType />
                </Box>
                <Box>
                    <Tooltip
                        title={hasWorksSelected ? "Não é possível alterar essa informação porque já existem itens vinculados à fatura." : null}
                        placement="right"
                        arrow
                        disableHoverListener={!hasWorksSelected}
                    >
                        <FormControl>
                            <RadioGroup
                                aria-labelledby="invoice-type-label"
                                name="invoice-type"
                                value={data.invoiceType || ""}
                                onChange={(e) =>
                                    setData((prevState) => ({
                                        ...prevState,
                                        invoiceType: e.target.value,
                                    })
                                    )}
                            >

                                <FormControlLabel value="has_work" control={<Radio />} disabled={hasWorksSelected} label="Com vínculo" />
                                <FormControlLabel value="without_work" control={<Radio />} disabled={hasWorksSelected} label="Sem vínculo" />
                            </RadioGroup>
                        </FormControl>
                    </Tooltip>
                </Box>
            </Box>
            <Box>
                <Typography variant='caption' sx={{color:'error.main'}}><b>*</b> Indica um campo obrigatório </Typography>
            </Box>
        </Box>

    )
}

export default InvoiceBasicData;